import CustomAutoComplete from "@/modules/common/components/input/dropdowns/auto-complete";
import StyledTextFieldWithTitle from "@/modules/common/components/input/styled-text-field-with-title";
import Textarea from "@/modules/common/components/input/text-area";
import { handleStringToBooleanValue } from "@/modules/common/utils";
import { CurrencyCode } from "@/modules/property-tracks/dtos";
import { ViewTypes } from "@/modules/property-tracks/types";
import { hourOptions } from "@/modules/property-tracks/utils/listing";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { Control, Controller, useWatch } from "react-hook-form";
import ExceptionalPrices from "./exceptional-prices";

const currencyOptions = Object.entries(CurrencyCode).map(([key, value]) => ({
  label: value,
  value: key,
}));

const viewTypeOptions = Object.entries(ViewTypes).map(([key, label]) => ({
  label,
  value: key,
}));

export default function HospitalityListingForm({
  control,
  roomTypeOptions,
}: {
  control: Control<any>;
  roomTypeOptions: { label: string; value: string }[];
}) {
  const bedCounts = useWatch({
    control,
    name: ["singleBedCount", "kingBedCount", "twinBedCount", "queenBedCount", "sofaBedCount"],
  });
  const totalBedCount = bedCounts.reduce((sum: number, val) => sum + (Number(val) || 0), 0);

  return (
    <Box
      sx={(t) => ({
        display: "flex",
        flexDirection: "column",
        gap: t.nawy.spacing.sm,
      })}
    >
      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.xs,
        })}
      >
        <Typography
          sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
        >
          Listing Info
        </Typography>
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              title="Listing Name *"
              placeholder="Listing Name"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="nameAr"
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              title="Listing Name (Arabic)"
              placeholder="اسم القائمة"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="description"
          render={({ field, fieldState }) => (
            <Textarea
              value={field.value}
              onValueChange={field.onChange}
              label="Description"
              placeholder="Description"
              isError={!!fieldState.error}
              errorMsg={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="descriptionAr"
          render={({ field, fieldState }) => (
            <Textarea
              value={field.value}
              onValueChange={field.onChange}
              label="Description (Arabic)"
              placeholder="الوصف"
              isError={!!fieldState.error}
              errorMsg={fieldState.error?.message}
            />
          )}
        />
      </Box>
      <Divider
        sx={(t) => ({
          backgroundColor: t.nawy.colors.semantic.layer.accent,
        })}
      />

      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.xs,
        })}
      >
        <Typography
          sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
        >
          Pricing
        </Typography>
        <Controller
          control={control}
          name="price"
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Base Price *"
              placeholder="Base Price"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <ExceptionalPrices control={control} />
        <Controller
          control={control}
          name="priceForExtraPerson"
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Price for Extra Person"
              placeholder="Price for Extra Person"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="currencyCode"
          render={({ field, fieldState }) => (
            <CustomAutoComplete
              value={field.value}
              options={currencyOptions}
              onChange={(value) => field.onChange(value)}
              placeholder="Currency *"
              label="Currency *"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Box>
      <Divider
        sx={(t) => ({
          backgroundColor: t.nawy.colors.semantic.layer.accent,
        })}
      />

      <Typography
        sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
      >
        Markups
      </Typography>
      <Controller
        control={control}
        name="airbnbOfficialListingMarkup"
        render={({ field, fieldState }) => (
          <StyledTextFieldWithTitle
            {...field}
            formatNumeric
            title="Airbnb Official Listing Markup"
            placeholder="Airbnb Markup"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            slotProps={{
              input: {
                endAdornment: <Typography>%</Typography>,
              },
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="bookingEngineMarkup"
        render={({ field, fieldState }) => (
          <StyledTextFieldWithTitle
            {...field}
            formatNumeric
            title="Booking Engine Markup"
            placeholder="Booking Engine Markup"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            slotProps={{
              input: {
                endAdornment: <Typography>%</Typography>,
              },
            }}
          />
        )}
      />
      <Divider
        sx={(t) => ({
          backgroundColor: t.nawy.colors.semantic.layer.accent,
        })}
      />

      <Typography
        sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
      >
        Stay Details
      </Typography>
      <Controller
        control={control}
        name="roomType"
        render={({ field, fieldState }) => (
          <CustomAutoComplete
            value={field.value}
            options={roomTypeOptions}
            onChange={(value) => field.onChange(value)}
            placeholder="Room Type"
            label="Room Type"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Box sx={{ display: "flex", gap: "12px" }}>
        <Controller
          control={control}
          name="minNights"
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Min Nights"
              placeholder="Min"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="maxNights"
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Max Nights"
              placeholder="Max"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Box>
      <Divider
        sx={(t) => ({
          backgroundColor: t.nawy.colors.semantic.layer.accent,
        })}
      />

      <Typography
        sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
      >
        Check-in / Check-out
      </Typography>
      <Box sx={{ display: "flex", gap: "12px" }}>
        <Controller
          control={control}
          name="checkInTimeStart"
          render={({ field, fieldState }) => (
            <CustomAutoComplete
              value={field.value}
              options={hourOptions}
              onChange={(value) => field.onChange(value)}
              placeholder="Check-in Start"
              label="Check-in Start"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="checkInTimeEnd"
          render={({ field, fieldState }) => (
            <CustomAutoComplete
              value={field.value}
              options={hourOptions}
              onChange={(value) => field.onChange(value)}
              placeholder="Check-in End"
              label="Check-in End"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Box>
      <Controller
        control={control}
        name="checkOutTime"
        render={({ field, fieldState }) => (
          <CustomAutoComplete
            value={field.value}
            options={hourOptions}
            onChange={(value) => field.onChange(value)}
            placeholder="Check-out Time"
            label="Check-out Time"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Divider
        sx={(t) => ({
          backgroundColor: t.nawy.colors.semantic.layer.accent,
        })}
      />

      <Typography
        sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
      >
        Room Details
      </Typography>
      <Controller
        control={control}
        name="maxNumberOfGuests"
        render={({ field, fieldState }) => (
          <StyledTextFieldWithTitle
            {...field}
            formatNumeric
            title="Max Number of Guests"
            placeholder="Max Number of Guests"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <StyledTextFieldWithTitle
        title="Bed Count"
        value={totalBedCount}
        fullWidth
        size="small"
        slotProps={{ input: { readOnly: true } }}
      />
      <Controller
        control={control}
        name="singleBedCount"
        render={({ field, fieldState }) => (
          <StyledTextFieldWithTitle
            {...field}
            formatNumeric
            title="Single Bed Count"
            placeholder="Single bed count"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="kingBedCount"
        render={({ field, fieldState }) => (
          <StyledTextFieldWithTitle
            {...field}
            formatNumeric
            title="King Bed Count"
            placeholder="King bed count"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="twinBedCount"
        render={({ field, fieldState }) => (
          <StyledTextFieldWithTitle
            {...field}
            formatNumeric
            title="Twin Bed Count"
            placeholder="Twin bed count"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="queenBedCount"
        render={({ field, fieldState }) => (
          <StyledTextFieldWithTitle
            {...field}
            formatNumeric
            title="Queen Bed Count"
            placeholder="Queen bed count"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="sofaBedCount"
        render={({ field, fieldState }) => (
          <StyledTextFieldWithTitle
            {...field}
            formatNumeric
            title="Sofa Bed Count"
            placeholder="Sofa bed count"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="viewType"
        render={({ field, fieldState }) => (
          <CustomAutoComplete
            value={field.value}
            options={viewTypeOptions}
            onChange={(value) => field.onChange(value)}
            placeholder="View Type"
            label="View Type"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Divider
        sx={(t) => ({
          backgroundColor: t.nawy.colors.semantic.layer.accent,
        })}
      />

      <Typography
        sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
      >
        House Rules
      </Typography>
      <Controller
        control={control}
        name="isExtraBedAllowed"
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                checked={field.value ?? false}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            }
            label="Extra Bed Allowed"
          />
        )}
      />
      <Controller
        control={control}
        name="isSmokingAllowed"
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                checked={field.value ?? false}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            }
            label="Smoking Allowed"
          />
        )}
      />
      <Controller
        control={control}
        name="isPetsAllowed"
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                checked={field.value ?? false}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            }
            label="Pets Allowed"
          />
        )}
      />
      <Controller
        control={control}
        name="isPartiesAllowed"
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                checked={field.value ?? false}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            }
            label="Parties Allowed"
          />
        )}
      />
      <Divider
        sx={(t) => ({
          backgroundColor: t.nawy.colors.semantic.layer.accent,
        })}
      />

      <Typography
        sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
      >
        Status
      </Typography>
      <Controller
        control={control}
        name="isActive"
        render={({ field, fieldState }) => (
          <Box>
            <Typography
              sx={(t) => t.nawy.typography.english.variants.body.sm.regular}
            >
              Is this listing active? *
            </Typography>
            <FormControl component="fieldset" error={!!fieldState.error}>
              <RadioGroup
                row
                name={field.name}
                value={field.value == null ? "" : String(field.value)}
                onChange={(e) =>
                  field.onChange(handleStringToBooleanValue(e.target.value))
                }
              >
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="Yes"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="No"
                />
              </RadioGroup>
              {fieldState.error && (
                <FormHelperText>{fieldState.error.message}</FormHelperText>
              )}
            </FormControl>
          </Box>
        )}
      />

      <Controller
        control={control}
        name="instantBookable"
        render={({ field, fieldState }) => (
          <Box>
            <Typography
              sx={(t) => t.nawy.typography.english.variants.body.sm.regular}
            >
              Is this listing instantly bookable? *
            </Typography>
            <FormControl component="fieldset" error={!!fieldState.error}>
              <RadioGroup
                row
                name={field.name}
                value={field.value == null ? "" : String(field.value)}
                onChange={(e) =>
                  field.onChange(handleStringToBooleanValue(e.target.value))
                }
              >
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="Yes"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="No"
                />
              </RadioGroup>
              {fieldState.error && (
                <FormHelperText>{fieldState.error.message}</FormHelperText>
              )}
            </FormControl>
          </Box>
        )}
      />

      <Controller
        control={control}
        name="isFeatured"
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                checked={field.value ?? false}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            }
            label="Featured"
          />
        )}
      />
    </Box>
  );
}
